import { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Heart, MessageCircle, Share2, Send, Volume2, VolumeX, X, Play, Pause, Flag } from 'lucide-react';
const ReportModal = lazy(() => import('../components/moderation/ReportModal'));
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import DonationButton from '../components/post/DonationButton';
import ShareMenu from '../components/share/ShareMenu';
import { resolveMedia } from '../components/social/discovery';

function fmtTime(s) {
  if (!isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

// Player fills the dynamic viewport below the header, accounting for safe-area-inset-top
const PLAYER_HEIGHT = 'calc(100dvh - var(--header-h, 3.5rem))';

export default function ReelsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reels, setReels] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [muted, setMuted] = useState(true);
  const [paused, setPaused] = useState(false);
  const [immersive, setImmersive] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const seekBarRef = useRef(null);

  // Refs for synchronous state access in event-handler closures
  const draggingRef = useRef(false);
  const showCommentsRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragProgress, setDragProgress] = useState(0);
  const [reportOpen, setReportOpen] = useState(false);
  const [commentReportId, setCommentReportId] = useState(null);

  useEffect(() => {
    api.get('/posts/reels?limit=20')
      .then((r) => setReels(r.data))
      .catch((e) => console.error('Error fetching sagas:', e))
      .finally(() => setLoading(false));
  }, []);

  // Keep showCommentsRef in sync
  useEffect(() => { showCommentsRef.current = showComments; }, [showComments]);

  // Reset state on reel change
  useEffect(() => {
    setCurrentTime(0);
    setDuration(0);
    setPaused(false);
    setImmersive(false);
    setShowComments(false);
  }, [currentIndex]);

  // Sync paused to video element
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (paused) v.pause();
    else v.play().catch(() => {});
  }, [paused, currentIndex]);

  const handleLike = async (reelId) => {
    const reel = reels.find(r => r.id === reelId);
    if (!reel) return;
    try {
      if (reel.is_liked) {
        await api.delete(`/posts/${reelId}/like`);
        setReels(prev => prev.map(r =>
          r.id === reelId ? { ...r, is_liked: false, likes_count: r.likes_count - 1 } : r
        ));
      } else {
        await api.post(`/posts/${reelId}/like`);
        setReels(prev => prev.map(r =>
          r.id === reelId ? { ...r, is_liked: true, likes_count: r.likes_count + 1 } : r
        ));
      }
    } catch {
      toast.error('Failed to update like');
    }
  };

  const handleMessage = (reel) => {
    if (!reel?.user?.id) return;
    navigate(`/messages?user=${reel.user.id}`);
  };

  const openComments = async (reel) => {
    setShowComments(true);
    setComments([]);
    try {
      const res = await api.get(`/posts/${reel.id}/comments`);
      setComments(res.data);
    } catch {
      console.error('Error loading comments');
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    const reel = reels[currentIndex];
    if (!newComment.trim() || commentLoading || !reel) return;
    setCommentLoading(true);
    try {
      const res = await api.post(`/posts/${reel.id}/comments`, { content: newComment.trim() });
      setComments(prev => [res.data, ...prev]);
      setNewComment('');
      setReels(prev => prev.map(r => r.id === reel.id ? { ...r, comments_count: (r.comments_count || 0) + 1 } : r));
    } catch {
      toast.error('Failed to post comment');
    } finally {
      setCommentLoading(false);
    }
  };

  const goToNext = useCallback(() => {
    setCurrentIndex(prev => (prev < reels.length - 1 ? prev + 1 : prev));
  }, [reels.length]);

  const goToPrev = useCallback(() => {
    setCurrentIndex(prev => (prev > 0 ? prev - 1 : prev));
  }, []);

  // Container-level touch/wheel navigation — blocked by seekbar drag or open comments
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let startY = 0;
    let startX = 0;

    const onStart = (e) => {
      startY = e.touches[0].clientY;
      startX = e.touches[0].clientX;
    };

    const onEnd = (e) => {
      // CRITICAL: check ref, not state — state may be stale in closure
      if (draggingRef.current || showCommentsRef.current) return;
      const dy = startY - e.changedTouches[0].clientY;
      const dx = Math.abs(startX - e.changedTouches[0].clientX);
      // Require clearly vertical and at least 60px to avoid accidental nav
      if (Math.abs(dy) > 60 && Math.abs(dy) > dx * 1.5) {
        dy > 0 ? goToNext() : goToPrev();
      }
    };

    const onWheel = (e) => {
      if (showCommentsRef.current) return;
      e.deltaY > 0 ? goToNext() : goToPrev();
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchend', onEnd, { passive: false });
    el.addEventListener('wheel', onWheel, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('wheel', onWheel);
    };
  }, [goToNext, goToPrev]);

  // Seekbar seek helpers
  const seekTo = useCallback((clientX) => {
    const bar = seekBarRef.current;
    const v = videoRef.current;
    if (!bar || !v || !duration) return;
    const rect = bar.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    v.currentTime = pct * duration;
    setCurrentTime(pct * duration);
    setDragProgress(pct * 100);
  }, [duration]);

  const startDrag = useCallback(() => {
    draggingRef.current = true;
    setIsDragging(true);
  }, []);

  const endDrag = useCallback(() => {
    draggingRef.current = false;
    setIsDragging(false);
  }, []);

  const onSeekMouseDown = useCallback((e) => {
    e.stopPropagation();
    startDrag();
    seekTo(e.clientX);
    const onMove = (ev) => seekTo(ev.clientX);
    const onUp = () => {
      endDrag();
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [seekTo, startDrag, endDrag]);

  const onSeekTouchStart = useCallback((e) => {
    e.stopPropagation();
    startDrag();
    seekTo(e.touches[0].clientX);
    const onMove = (ev) => {
      ev.stopPropagation();
      seekTo(ev.touches[0].clientX);
    };
    const onEnd = (ev) => {
      ev.stopPropagation();
      endDrag();
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
    };
    // Use non-passive so we can stopPropagation
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd, { passive: false });
  }, [seekTo, startDrag, endDrag]);

  const handleVideoTap = useCallback((e) => {
    if (e.target !== e.currentTarget) return;
    setImmersive(prev => !prev);
  }, []);

  /* ==================== RENDERS ==================== */

  if (loading) {
    return (
      <div className="w-full bg-black flex items-center justify-center" style={{ height: PLAYER_HEIGHT }}>
        <div className="animate-pulse-glow w-16 h-16 rounded-full bg-primary/20" />
      </div>
    );
  }

  if (reels.length === 0) {
    return (
      <div
        className="w-full bg-background flex flex-col items-center justify-center px-4 min-[900px]:ml-60"
        style={{ height: PLAYER_HEIGHT }}
        data-testid="reels-page"
      >
        <div className="w-20 h-20 mb-4 rounded-full bg-muted flex items-center justify-center">
          <span className="text-4xl">🎬</span>
        </div>
        <h2 className="font-heading text-xl font-bold mb-2">No Sagas Yet</h2>
        <p className="text-muted-foreground text-center">
          Be the first to create a Saga! Upload a vertical video to get started.
        </p>
      </div>
    );
  }

  const reel = reels[currentIndex];
  const progress = isDragging ? dragProgress : (duration > 0 ? (currentTime / duration) * 100 : 0);

  return (
    <div
      ref={containerRef}
      className="bg-black overflow-hidden min-[900px]:ml-60 flex justify-center"
      style={{ height: PLAYER_HEIGHT }}
      data-testid="reels-page"
    >
      {/* Saga column — constrained width, centered */}
      <div className="relative h-full w-full" style={{ maxWidth: 480 }}>

        {/* Video / image area */}
        <div
          className="absolute inset-0 flex items-center justify-center bg-black cursor-pointer overflow-hidden"
          onClick={handleVideoTap}
        >
          {reel.media_urls?.length > 0 ? (
            reel.media_type === 'video' ? (
              <video
                ref={videoRef}
                key={reel.id}
                src={resolveMedia(reel.media_urls[0])}
                poster={reel.poster_url ? resolveMedia(reel.poster_url) : undefined}
                className="max-h-full max-w-full object-contain"
                loop
                autoPlay
                muted={muted}
                playsInline
                onTimeUpdate={(e) => {
                  if (!draggingRef.current) setCurrentTime(e.currentTarget.currentTime);
                }}
                onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
                onPlay={() => setPaused(false)}
                onPause={() => setPaused(true)}
                onError={(e) => console.error('Video error', e.currentTarget.error)}
              />
            ) : (
              <img
                src={resolveMedia(reel.media_urls[0])}
                alt=""
                className="max-h-full max-w-full object-contain"
              />
            )
          ) : (
            <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-primary/20 to-accent/20 p-8">
              <p className="text-xl text-center text-white">{reel.content}</p>
            </div>
          )}
        </div>

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none" />

        {/* ── HUD — hidden in immersive mode ── */}
        <div
          className={`absolute inset-0 flex flex-col justify-end pointer-events-none transition-opacity duration-200 ${immersive ? 'opacity-0' : 'opacity-100'}`}
          aria-hidden={immersive}
        >
          {/* Bottom info + seekbar */}
          <div className="pointer-events-auto pb-safe" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
            {/* Author + caption */}
            <div className="px-4 pb-2 pr-16">
              <Link to={`/profile/${reel.user?.username}`} className="flex items-center gap-2.5 mb-2">
                <Avatar className="w-10 h-10 border-2 border-white/80">
                  <AvatarImage src={reel.user?.avatar} />
                  <AvatarFallback>{reel.user?.display_name?.[0]}</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="font-semibold text-white text-sm truncate">{reel.user?.display_name}</p>
                  <p className="text-xs text-white/60 truncate">@{reel.user?.username}</p>
                </div>
              </Link>
              <p className="text-white text-sm line-clamp-2 leading-relaxed">{reel.content}</p>
              {reel.hashtags?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {reel.hashtags.map(tag => (
                    <span key={tag} className="text-primary text-xs">#{tag}</span>
                  ))}
                </div>
              )}
            </div>

            {/* Seekbar row */}
            {reel.media_type === 'video' && (
              <div className="px-4 pb-4 flex items-center gap-3">
                {/* Play/Pause */}
                <button
                  onClick={(e) => { e.stopPropagation(); setPaused(p => !p); }}
                  className="w-10 h-10 flex items-center justify-center text-white shrink-0"
                  aria-label={paused ? 'Play' : 'Pause'}
                  data-testid="reel-play-pause"
                >
                  {paused
                    ? <Play className="w-6 h-6 fill-white" />
                    : <Pause className="w-6 h-6 fill-white" />}
                </button>

                {/* Timeline */}
                <div className="flex-1 flex items-center gap-2">
                  <span className="text-white text-[11px] tabular-nums shrink-0">{fmtTime(currentTime)}</span>
                  {/* Expanded tap target wrapper */}
                  <div
                    className="flex-1 py-3 cursor-pointer"
                    onMouseDown={onSeekMouseDown}
                    onTouchStart={onSeekTouchStart}
                    style={{ touchAction: 'none' }}
                  >
                    <div
                      ref={seekBarRef}
                      className="h-1 bg-white/30 rounded-full relative pointer-events-none"
                      role="slider"
                      aria-label="Seek"
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={Math.round(progress)}
                      data-testid="reel-seekbar"
                    >
                      <div
                        className="absolute top-0 left-0 h-full bg-white rounded-full"
                        style={{ width: `${progress}%` }}
                      />
                      {/* Drag handle — larger for touch */}
                      <div
                        className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow -translate-x-1/2"
                        style={{ left: `${progress}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-white text-[11px] tabular-nums shrink-0">{fmtTime(duration)}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Right action rail ── */}
        <div
          className={`absolute right-3 flex flex-col items-center gap-5 z-10 transition-opacity duration-200 ${immersive ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
          style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 5rem)' }}
        >
          {/* Like */}
          <button onClick={() => handleLike(reel.id)} className="flex flex-col items-center gap-0.5" data-testid="reel-like-btn">
            <div className={`w-11 h-11 rounded-full flex items-center justify-center transition-colors ${reel.is_liked ? 'bg-red-500' : 'bg-white/15 backdrop-blur-sm'}`}>
              <Heart className={`w-5 h-5 ${reel.is_liked ? 'text-white fill-white' : 'text-white'}`} />
            </div>
            <span className="text-white text-[10px] font-medium">{reel.likes_count}</span>
          </button>

          {/* Comment */}
          <button onClick={() => openComments(reel)} className="flex flex-col items-center gap-0.5" data-testid="reel-comment-btn">
            <div className="w-11 h-11 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            <span className="text-white text-[10px] font-medium">{reel.comments_count || 0}</span>
          </button>

          {/* Message */}
          <button onClick={() => handleMessage(reel)} className="flex flex-col items-center gap-0.5" data-testid="reel-message-btn">
            <div className="w-11 h-11 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center">
              <Send className="w-5 h-5 text-white" />
            </div>
            <span className="text-white text-[10px]">Message</span>
          </button>

          {/* Donate */}
          <DonationButton
            recipientId={reel.user?.id}
            recipientName={reel.user?.display_name}
            variant="reel"
          />

          {/* Share */}
          <ShareMenu postId={reel.id} text={reel.content} align="end" testId="reel-share-menu">
            <button className="flex flex-col items-center gap-0.5" data-testid="reel-share-btn">
              <div className="w-11 h-11 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center">
                <Share2 className="w-5 h-5 text-white" />
              </div>
              <span className="text-white text-[10px]">Share</span>
            </button>
          </ShareMenu>

          {/* Mute */}
          <button onClick={() => setMuted(!muted)} className="flex flex-col items-center" data-testid="reel-mute-btn">
            <div className="w-11 h-11 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center">
              {muted ? <VolumeX className="w-5 h-5 text-white" /> : <Volume2 className="w-5 h-5 text-white" />}
            </div>
          </button>

          {/* Report (own reels excluded) */}
          {user && reel.user?.id !== user.id && (
            <button onClick={() => setReportOpen(true)} className="flex flex-col items-center gap-0.5" data-testid="reel-report-btn">
              <div className="w-11 h-11 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center">
                <Flag className="w-5 h-5 text-white/70" />
              </div>
              <span className="text-white/70 text-[10px]">Report</span>
            </button>
          )}
        </div>

        <Suspense fallback={null}>
          <ReportModal
            open={reportOpen}
            onClose={() => setReportOpen(false)}
            targetType="post"
            targetId={reel.id}
            targetLabel="Saga"
          />
        </Suspense>

        <Suspense fallback={null}>
          <ReportModal
            open={!!commentReportId}
            onClose={() => setCommentReportId(null)}
            targetType="comment"
            targetId={commentReportId || ''}
            targetLabel="comment"
          />
        </Suspense>

        {/* ── Comments panel — positioned to never exceed viewport ── */}
        {showComments && (
          <div
            className="absolute inset-x-0 bottom-0 z-20 bg-[#0a050f]/97 backdrop-blur-xl border-t border-primary/30 rounded-t-2xl flex flex-col animate-in slide-in-from-bottom duration-200"
            style={{ maxHeight: 'min(75%, 70dvh)' }}
            data-testid="reel-comments-panel"
            onTouchStart={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            {/* Header — always visible */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 shrink-0">
              <h3 className="text-white font-semibold text-sm">
                {reel.comments_count || 0} Comments
              </h3>
              <button
                onClick={() => setShowComments(false)}
                className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-white"
                data-testid="reel-comments-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Scrollable list */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4 min-h-0" data-testid="reel-comments-list">
              {comments.length === 0 ? (
                <p className="text-white/50 text-sm text-center py-8">No comments yet. Be the first!</p>
              ) : (
                comments.map((comment) => (
                  <div key={comment.id} className="flex gap-3" data-testid="reel-comment-item">
                    <Avatar className="w-8 h-8 border border-primary/40 shrink-0">
                      <AvatarImage src={comment.user?.avatar} />
                      <AvatarFallback>{comment.user?.display_name?.[0]}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="text-white text-sm font-medium">{comment.user?.display_name}</p>
                      <p className="text-white/80 text-sm break-words">{comment.content}</p>
                      {comment.user?.id !== user?.id && (
                        <button
                          onClick={() => setCommentReportId(comment.id)}
                          className="text-white/30 hover:text-red-400 text-xs flex items-center gap-0.5 mt-0.5 transition-colors"
                          aria-label="Report comment"
                          data-testid={`reel-report-comment-${comment.id}`}
                        >
                          <Flag className="w-3 h-3" />
                          <span>Report</span>
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Input — always visible at bottom */}
            <form
              onSubmit={submitComment}
              className="flex items-center gap-2 px-4 py-3 border-t border-white/10 shrink-0"
              style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0px))' }}
            >
              <Input
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment…"
                className="bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-full"
                data-testid="reel-comment-input"
              />
              <Button
                type="submit"
                size="icon"
                disabled={!newComment.trim() || commentLoading}
                className="rounded-full shrink-0"
                data-testid="reel-comment-submit"
              >
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
